import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import { ADMISSION_COLORS as C } from "../../constants/admissionTheme";
import { getCrossDeviceUploadUrl } from "../../utils/crossDeviceQr";
import { createQrUploadSession, fetchQrPhoto } from "../../api/documentService";

function PhotoQrDialog({ open, onClose, onPhoto }) {
  const [uploadToken, setUploadToken] = useState(null);
  const [sessionError, setSessionError] = useState("");
  const [checking, setChecking] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  // Ignores a session response that arrives after the dialog closed or re-opened.
  const sessionRequestRef = useRef(0);

  const reset = useCallback(() => {
    sessionRequestRef.current += 1;
    setUploadToken(null);
    setSessionError("");
    setChecking(false);
    setStatusMsg("");
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const openSession = useCallback(async () => {
    reset();
    const requestNo = sessionRequestRef.current;
    try {
      const { token } = await createQrUploadSession("photo");
      if (requestNo !== sessionRequestRef.current) return;
      setUploadToken(token);
    } catch (err) {
      if (requestNo !== sessionRequestRef.current) return;
      setSessionError(
        err?.response?.data?.message || err?.message || "Could not generate the QR code.",
      );
    }
  }, [reset]);

  useEffect(() => {
    if (open) openSession();
    else reset();
  }, [open, openSession, reset]);

  const uploadUrl = getCrossDeviceUploadUrl(uploadToken, "photo");

  const checkUploadedPhoto = async () => {
    if (!uploadToken) return;
    setChecking(true);
    setStatusMsg("");
    try {
      const { found, dataUrl } = await fetchQrPhoto(uploadToken);
      if (found) {
        onPhoto(dataUrl);
        toast.success("Photo received from phone.");
        handleClose();
        return;
      }
      setStatusMsg("No photo uploaded yet. Upload it from the phone, then refresh.");
    } catch (err) {
      setStatusMsg(
        err?.response?.data?.message || err?.message || "Could not fetch the photo. Please try again.",
      );
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `2px solid ${C.info.bg}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <QrCodeScannerIcon sx={{ color: C.primary }} />
          <Typography fontWeight={700} color={C.primary}>
            Scan QR to Upload Photo
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ textAlign: "center", py: 3 }}>
        {uploadToken && (
          <>
            <Typography variant="body2" color={C.textSecondary} mb={2}>
              Scan this QR code with any phone, take or choose the patient's photo
              and upload it. Then tap <strong>Refresh</strong> below. The code is
              valid for 10 minutes and one upload.
            </Typography>

            <Box
              component="a"
              href={uploadUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-block",
                p: 2,
                border: `3px solid ${C.primary}`,
                borderRadius: 2,
                bgcolor: C.cardBg,
                mb: 2,
                textDecoration: "none",
              }}
            >
              <QRCodeSVG value={uploadUrl} size={220} level="L" includeMargin={false} />
            </Box>

            <Button
              variant="contained"
              fullWidth
              disabled={checking}
              onClick={checkUploadedPhoto}
              sx={{ py: 1.25 }}
            >
              {checking ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} color="inherit" />
                  Checking…
                </Box>
              ) : (
                "Refresh & Fetch Photo"
              )}
            </Button>

            {statusMsg && (
              <Typography
                variant="body2"
                color={C.textSecondary}
                sx={{ mt: 2, p: 1, bgcolor: C.pageBg, borderRadius: 1 }}
              >
                {statusMsg}
              </Typography>
            )}
          </>
        )}

        {sessionError && (
          <Box py={2}>
            <Typography variant="body2" color="error" mb={2}>
              {sessionError}
            </Typography>
            <Button variant="outlined" onClick={openSession}>
              Retry
            </Button>
          </Box>
        )}

        {!uploadToken && !sessionError && (
          <Box py={4}>
            <CircularProgress size={28} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default memo(PhotoQrDialog);
